// import { Node, Edge } from 'reactflow'; // Unused for now

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  definition: WorkflowDefinition;
  userId?: string;
  organizationId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowDefinition {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  variables: WorkflowVariable[];
}

// Define our own node type that's compatible with React Flow
export interface WorkflowNode {
  id: string;
  type: NodeType;
  position: { x: number; y: number };
  data: {
    title: string;
    description?: string;
    config: NodeConfig;
    isConnectable?: boolean;
  };
  style?: React.CSSProperties;
  className?: string;
  draggable?: boolean;
  selectable?: boolean;
  deletable?: boolean;
  width?: number;
  height?: number;
  zIndex?: number;
}

// Define our own edge type that's compatible with React Flow
export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  type: 'default' | 'conditional';
  data?: {
    condition?: 'true' | 'false' | 'success' | 'error';
    label?: string;
  };
}

export interface WorkflowVariable {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description: string;
  defaultValue?: any;
  required?: boolean;
}

export type NodeType = 'start' | 'teammate' | 'human' | 'api' | 'condition' | 'loop' | 'end';

export interface NodeConfig {
  // Start node config
  inputMapping?: Record<string, string>;

  // Teammate node config
  teammateId?: string;
  prompt?: string;
  contextVariables?: string[];

  // Human task node config
  assigneeId?: string;
  assigneeRole?: string;
  formFields?: FormField[];
  deadline?: string;
  title?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';

  // API node config
  url?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  bodyTemplate?: string;
  authentication?: {
    type: 'none' | 'bearer' | 'api_key' | 'basic';
    config: Record<string, string>;
  };
  timeout?: number;
  maxRetries?: number;

  // Condition node config
  condition?: string;
  truePath?: string;
  falsePath?: string;

  // Loop node config
  loopType?: 'for' | 'while' | 'forEach';
  iterations?: number;
  breakCondition?: string;

  // End node config
  outputMapping?: Record<string, string>;
  successCriteria?: string;
}

export interface FormField {
  name: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'date' | 'number';
  label: string;
  required: boolean;
  options?: string[];
  defaultValue?: any;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflowName?: string;
  taskId?: string;
  status: ExecutionStatus;
  inputData?: Record<string, any>;
  outputData?: Record<string, any>;
  currentNodeId?: string;
  executionLog: ExecutionLogEntry[];
  variables: Record<string, any>;
  startedAt: string;
  completedAt?: string;
  createdBy: string;
  organizationId?: string;
}

export type ExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'paused';

export interface ExecutionLogEntry {
  timestamp: string;
  nodeId?: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  metadata?: Record<string, any>;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
  definition: WorkflowDefinition;
  isPublic: boolean;
  createdBy: string;
  organizationId?: string;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  definition: WorkflowDefinition;
  isTemplate?: boolean;
}

export interface UpdateWorkflowRequest {
  name?: string;
  description?: string;
  definition?: WorkflowDefinition;
  isActive?: boolean;
}

export interface ExecuteWorkflowRequest {
  inputData?: Record<string, any>;
  taskId?: string;
}

export interface WorkflowValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  nodeId?: string;
  field?: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationWarning {
  nodeId?: string;
  field?: string;
  message: string;
  type: 'performance' | 'best_practice' | 'accessibility';
}

// Node color mapping for visual distinction
export const NODE_COLORS = {
  start: '#4CAF50', // Green
  teammate: '#2196F3', // Blue
  human: '#FF9800', // Orange
  api: '#9C27B0', // Purple
  condition: '#FFC107', // Amber
  loop: '#607D8B', // Blue Grey
  end: '#F44336' // Red
} as const;

// Node icons for the toolbar
export const NODE_ICONS = {
  start: 'play',
  teammate: 'bot',
  human: 'user',
  api: 'link',
  condition: 'branch',
  loop: 'repeat',
  end: 'stop'
} as const;